import React, {useState, useEffect} from 'react';
import {useParams, useNavigate, Link as RouterLink} from 'react-router-dom';
import {
    Box, Typography, Paper, CircularProgress, Grid, Button, Alert,
    TextField, FormControlLabel, Switch, Snackbar, Stack
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import apiService from '../../services/apiService';
import EntityCriteriaEditor from '../../components/EntityCriteriaEditor';
import {ProtectedFieldSet} from '../../context/AuthDisabledContext';
import Protected from '../../components/Protected';
import {ROLES} from '../../config/roles';

const LevelPlayPlacementEditPage = () => {
    const {id} = useParams();
    const navigate = useNavigate();
    const isNew = id === 'new';
    const requiredAction = isNew ? 'create' : 'update';

    const [placementName, setPlacementName] = useState('');
    const [enabled, setEnabled] = useState(true);
    const [award, setAward] = useState([]);

    const [loading, setLoading] = useState(!isNew);
    const [error, setError] = useState(null);
    const [showErrors, setShowErrors] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [snackbar, setSnackbar] = useState({open: false, message: ''});

    useEffect(() => {
        if (!isNew) {
            apiService.get(`/api/levelplay-placements/${id}`).then(data => {
                setPlacementName(data.placementName || '');
                setEnabled(data.enabled ?? true);
                setAward(data.award || []);
            })
                .catch(err => setError(err.message || 'Failed to load data'))
                .finally(() => setLoading(false));
        }
    }, [id, isNew]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setError(null);

        if (!placementName.trim()) {
            setError('Placement Name is required');
            setShowErrors(true);
            return;
        }
        // The placement award schema requires EXACTLY ONE entry. EntityCriteriaEditor.validate()
        // deliberately accepts an empty list (it is shared with minigame entryCost), so the
        // exactly-one rule must be enforced here, before delegating to the per-row validator.
        if (!award || award.length !== 1) {
            setError('Award: exactly one award entry is required');
            setShowErrors(true);
            return;
        }
        const awardError = EntityCriteriaEditor.validate(award);
        if (awardError) {
            setError(`Award: ${awardError}`);
            setShowErrors(true);
            return;
        }
        setShowErrors(false);

        setIsSaving(true);
        const data = {
            id: isNew ? null : id,
            placementName: placementName.trim(),
            enabled,
            award,
        };

        try {
            const apiCall = isNew
                ? apiService.post('/api/levelplay-placements', data)
                : apiService.put(`/api/levelplay-placements/${id}`, data);
            await apiCall;
            setSnackbar({open: true, message: 'Placement saved successfully!'});
            setTimeout(() => navigate('/levelplay-placements'), 800);
        } catch (err) {
            setError(err.message || 'Failed to save placement');
        } finally {
            setIsSaving(false);
        }
    };

    if (loading) return <Box sx={{display: 'flex', justifyContent: 'center', p: 4}}><CircularProgress/></Box>;

    return (
        <Box sx={{p: 2}} component="form" onSubmit={handleSubmit}>
            <Typography variant="h4" gutterBottom>
                {isNew ? 'Create Placement' : `Edit Placement: ${placementName}`}
            </Typography>

            <Protected component={LevelPlayPlacementEditPage} action={requiredAction} mode="disable">
                <ProtectedFieldSet>
                    <Grid container spacing={3}>
                        <Grid size={{xs: 12, md: 6}}>
                            <Paper sx={{p: 2}}>
                                <Typography variant="h6" gutterBottom>Configuration</Typography>
                                <Stack spacing={2}>
                                    <TextField required fullWidth label="Placement Name" value={placementName}
                                               onChange={e => setPlacementName(e.target.value)}
                                               helperText="The LevelPlay placementName sent by the ad callback. Must be unique."
                                               error={showErrors && !placementName.trim()}/>
                                    <FormControlLabel
                                        control={<Switch checked={enabled} onChange={e => setEnabled(e.target.checked)}/>}
                                        label="Enabled"/>
                                </Stack>
                            </Paper>
                        </Grid>

                        <Grid size={12}>
                            <Paper sx={{p: 2}}>
                                <Typography variant="h6" gutterBottom>Award</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                                    What the player receives per rewarded ad for this placement. Exactly one entry.
                                    The amount is nominal — the real amount comes from the ad callback.
                                </Typography>
                                <EntityCriteriaEditor value={award} onChange={setAward} preset="reward" showErrors={showErrors}/>
                            </Paper>
                        </Grid>
                    </Grid>

                    {error && <Alert severity="error" sx={{mt: 3}}>{error}</Alert>}

                    <Stack direction="row" spacing={2} sx={{mt: 3}}>
                        <Protected component={LevelPlayPlacementEditPage} action={requiredAction}>
                            <Button type="submit" variant="contained"
                                    startIcon={isSaving ? <CircularProgress size={20}/> : <SaveIcon/>}
                                    disabled={isSaving}>
                                {isSaving ? 'Saving...' : 'Save Placement'}
                            </Button>
                        </Protected>
                        <Button component={RouterLink} to="/levelplay-placements" variant="outlined" startIcon={<ArrowBackIcon/>}>
                            Back to List
                        </Button>
                    </Stack>

                    <Snackbar
                        open={snackbar.open}
                        autoHideDuration={6000}
                        onClose={() => setSnackbar({open: false, message: ''})}
                        message={snackbar.message}
                    />
                </ProtectedFieldSet>
            </Protected>
        </Box>
    );
};

LevelPlayPlacementEditPage.abilities = {
    create: [ROLES.ADMINS],
    update: [ROLES.ADMINS],
};

export default LevelPlayPlacementEditPage;
